/**
 * providers/mockProviders.ts — a placeholder provider for every domain.
 *
 * Each is `live:false` and returns deterministic normalized records. They are
 * registered as the default fallback so the engine (and all downstream engines)
 * works fully offline. Swapping in a real integration is one register() call.
 */

import type { DomainResultMap, FusionDomain } from '../models';
import { FUSION_DOMAINS } from '../models';
import type { FusionProvider, FusionQuery, ProviderMeta } from './types';
import {
  genCommunity,
  genCoworking,
  genCurrency,
  genEvents,
  genHousing,
  genMetric,
  genPlaces,
  genRestaurants,
  genSafety,
  genTransportation,
  genWeather,
} from './mockData';

const DEFAULT_LIMIT = 8;

function meta(domain: FusionDomain): ProviderMeta {
  return {
    id: `mock-${domain}`,
    name: `Placeholder ${domain}`,
    domain,
    live: false,
    priority: 100, // real providers register with lower numbers to win the chain
    baseConfidence: 60, // placeholder trust — real providers score higher
  };
}

/** Route a query to the right generator, always returning normalized records. */
function generate(query: FusionQuery, providerId: string): DomainResultMap[FusionDomain][] {
  const { location, domain } = query;
  const limit = query.limit ?? DEFAULT_LIMIT;
  const now = query.now ?? 0;

  switch (domain) {
    case 'weather':
      return [genWeather(location, now, providerId)];
    case 'currency':
      return [genCurrency(location, now, providerId)];
    case 'safety':
    case 'travelAdvisory':
      return [genSafety(location, providerId)];
    case 'communityReviews':
      return [genCommunity(location, providerId, String(query.params?.placeId ?? location.locationId))];
    case 'internetSpeed':
    case 'airQuality':
    case 'costOfLiving':
    case 'visa':
      return [genMetric(location, domain, providerId)];
    case 'restaurant':
      return genRestaurants(location, limit, providerId);
    case 'coworking':
      return genCoworking(location, limit, providerId);
    case 'housing':
    case 'hotel':
    case 'hostel':
    case 'monthlyRental':
      return genHousing(location, domain, limit, providerId);
    case 'events':
      return genEvents(location, now, limit, providerId);
    case 'transportation':
    case 'publicTransit':
      return genTransportation(location, limit, providerId);
    default:
      // Every place-like domain (maps, atm, bank, hospital, gym, grocery, …).
      return genPlaces(location, domain, limit, providerId);
  }
}

/** Build the placeholder provider for a single domain. */
export function createMockProvider<D extends FusionDomain>(domain: D): FusionProvider<D> {
  const providerMeta = meta(domain);
  return {
    meta: providerMeta,
    async fetch(query: FusionQuery) {
      return generate(query, providerMeta.id) as DomainResultMap[D][];
    },
  };
}

/** One mock provider per supported domain. */
export const MOCK_PROVIDERS: FusionProvider[] = FUSION_DOMAINS.map((d) => createMockProvider(d));
